// apps/provider/src/services/api/documentationConfig.js
// Organization documentation configuration methods (specialty template routing, note type rules, carry-forward)
//
// Methods: getDocumentationConfigs, getDocumentationConfig, createDocumentationConfig,
//          updateDocumentationConfig, deleteDocumentationConfig
//
// Response types: @prism-ehr/shared → OrganizationDocumentationConfigResponse

/**
 * @returns {Object} Documentation config domain methods (bound to `this` of the composed apiService)
 */
export function createDocumentationConfigMethods() {
  return {
    /**
     * Get all documentation configs for an organization.
     *
     * Xano: GET /organization-documentation-config
     *
     * @param {string} organizationId
     * @returns {Promise<import('@prism-ehr/shared').OrganizationDocumentationConfigResponse[]>}
     */
    async getDocumentationConfigs(organizationId) {
      return this.request(`/organization-documentation-config?organization_id=${organizationId}`, {
        method: 'GET'
      });
    },

    /**
     * Get a single documentation config by ID.
     *
     * Xano: GET /organization-documentation-config/{config_id}
     *
     * @param {string} configId
     * @returns {Promise<import('@prism-ehr/shared').OrganizationDocumentationConfigResponse>}
     */
    async getDocumentationConfig(configId) {
      return this.request(`/organization-documentation-config/${configId}`, {
        method: 'GET'
      });
    },

    /**
     * Create a new documentation config for a specialty.
     *
     * Xano: POST /organization-documentation-config
     *
     * @param {Object} configData
     * @returns {Promise<import('@prism-ehr/shared').OrganizationDocumentationConfigResponse>}
     */
    async createDocumentationConfig(configData) {
      return this.request('/organization-documentation-config', {
        method: 'POST',
        body: JSON.stringify(configData)
      });
    },

    /**
     * Update a documentation config.
     *
     * Xano: PATCH /organization-documentation-config/{config_id}
     *
     * @param {string} configId
     * @param {Object} configData
     * @returns {Promise<import('@prism-ehr/shared').OrganizationDocumentationConfigResponse>}
     */
    async updateDocumentationConfig(configId, configData) {
      return this.request(`/organization-documentation-config/${configId}`, {
        method: 'PATCH',
        body: JSON.stringify(configData)
      });
    },

    /**
     * Delete a documentation config.
     *
     * Xano: DELETE /organization-documentation-config/{config_id}
     *
     * @param {string} configId
     * @returns {Promise<null>}
     */
    async deleteDocumentationConfig(configId) {
      return this.request(`/organization-documentation-config/${configId}`, {
        method: 'DELETE'
      });
    },
  };
}
